import { useFlags } from 'launchdarkly-react-client-sdk'
import { capitalize } from 'lodash'
import React, { useEffect, useState } from 'react'

import Time from 'components/attacks/details/Time'
import { getAttackStageColor } from 'components/attacks/utils/attack-stages'
import { ExecutionHostStatus } from 'components/clients/HostStatus'
import Dot from 'components/common/Dot'
import Flex from 'components/common/Flex'
import Text from 'components/common/Text'
import TrackedExternalLink from 'components/common/TrackedExternalLink'
import Icon from 'components/Icon'
import theme from 'theme'
import { Execution as ExecutionModel } from 'types/api/attacks/details/execution'

const MetricsLinks: React.FC<{
  attackId?: string
  metricsLinks?: Record<string, any>
}> = ({ attackId, metricsLinks = {} }) => {
  if (!Object.keys(metricsLinks).length) return null

  return (
    <>
      {Object.entries(metricsLinks).map(([provider, link]) => {
        return (
          <div key={provider}>
            <TrackedExternalLink
              css={{ display: 'inline' }}
              eventName="ExecutionMetricsClick"
              eventProperties={{
                provider,
                attackId
              }}
              href={link}
            >
              <Text underline as="small" color="normal" weight="normal">
                {capitalize(provider)} Metrics
              </Text>
            </TrackedExternalLink>
          </div>
        )
      })}
    </>
  )
}

type Event = {
  code: string
  link?: string
  message: string
  timestamp: string
}

const FormattedEvent: React.FC<{
  event: Event
}> = ({ event }) => (
  <div>
    {event.timestamp} - {event.message} -{' '}
    {event.link && (
      <TrackedExternalLink
        css={{
          marginTop: theme.space[1],
          fontSize: theme.fontSizes[1],
          fontWeight: theme.fontWeights[1]
        }}
        href={event.link}
      >
        {event.code}
      </TrackedExternalLink>
    )}
  </div>
)

const ExpandedOutput: React.FC<{
  children?: React.ReactNode
  title: string
}> = ({ children, title }) => {
  return (
    <div
      css={{ padding: theme.space[3], minHeight: 48 }}
      data-test-expanded-output={title}
    >
      <h3>{title}</h3>
      {children && (
        <code
          css={{
            display: 'block',
            border: theme.border.base,
            padding: theme.space[3],
            marginTop: theme.space[3],
            whiteSpace: 'pre-line',
            lineHeight: '2'
          }}
        >
          {children}
        </code>
      )}
    </div>
  )
}

const Execution: React.FC<{
  attackId?: string
  execution?: ExecutionModel & { error?: string; event_codes?: Event[] }
  isInitiallyOpen?: boolean
  resetViewAll?: () => void
  viewAll?: boolean
}> = ({
  attackId,
  execution,
  isInitiallyOpen = false,
  resetViewAll,
  viewAll
}) => {
  const {
    client_id: clientId,
    client_version: clientVersion,
    end_time: endTime,
    error = '',
    event_codes: eventCodes,
    guest_id: guestId,
    metrics_links: metricsLinks,
    output = '',
    stage,
    start_time: startTime
  } = execution
  const sanitizedOutput = output.replace('\n', '\u000A')
  const sanitizedError = error.replace('\n', '\u000A')
  const isExpandable = Boolean(sanitizedOutput) || Boolean(sanitizedError)
  const { exposeClientStatus: isExposeClientStatusEnabled } = useFlags()

  const [isOpen, setOpenValue] = useState(isInitiallyOpen)

  useEffect(() => {
    // this useEffect only cares about an execution with event codes
    if (!eventCodes?.length) return

    if (viewAll && !isOpen) {
      setOpenValue(true)
    }

    if (viewAll && isOpen) {
      // reset viewAll state so we don't keep trying to open the execution when a user
      // is trying to close it
      resetViewAll()
    }
  }, [eventCodes?.length, isOpen, resetViewAll, viewAll])

  return (
    <section
      css={{
        flex: 1,
        fontSize: theme.fontSizes[2],
        marginTop: theme.space[2]
      }}
      data-test-execution={clientId}
    >
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: '32px minmax(96px,1fr) minmax(96px,0.3fr) 48px',
          gap: theme.space[2],
          alignItems: 'center',
          padding: theme.space[2],
          paddingLeft: 0,
          transition: '150ms background-color',
          cursor: isExpandable ? 'pointer' : 'default',

          '&:hover': isExpandable && {
            backgroundColor: theme.colors.grayOverlay[0]
          }
        }}
        onClick={() => isExpandable && setOpenValue(!isOpen)}
      >
        <Dot
          color={getAttackStageColor(stage)}
          css={{ justifySelf: 'center' }}
        />
        <div>
          <label
            css={{
              flex: 1,
              cursor: isExpandable ? 'inherit' : 'default'
            }}
          >
            <Flex>
              <Text
                as="p"
                color="dark"
                css={{
                  marginRight: theme.space[2]
                }}
              >
                {clientId}
              </Text>
              <Text
                as="small"
                color="normal"
                css={{
                  marginRight: theme.space[2]
                }}
                weight="normal"
              >
                Gremlin v{clientVersion}
              </Text>
              {isExposeClientStatusEnabled &&
                execution.stage === 'Failed' &&
                execution?.client_status === 'UNHEALTHY' && (
                  <ExecutionHostStatus execution={execution} />
                )}
            </Flex>
            {guestId && (
              <Text
                as="small"
                css={{
                  wordBreak: 'break-all',
                  marginTop: theme.space[2]
                }}
              >
                Container ID: {guestId}
              </Text>
            )}
          </label>
          <MetricsLinks attackId={attackId} metricsLinks={metricsLinks} />
        </div>

        <div
          css={{
            display: 'grid',
            alignItems: 'center',
            justifyItems: 'end',
            columnGap: theme.space[2],
            gridTemplateColumns: '1fr 1fr',
            justifySelf: 'end',
            whiteSpace: 'nowrap'
          }}
        >
          {startTime && <Time label="Start" time={startTime} />}
          {endTime && <Time label="End" time={endTime} />}
        </div>
        {isExpandable && (
          <div
            css={{
              marginLeft: theme.space[3]
            }}
          >
            {isOpen ? (
              <Icon icon="chevronDown" />
            ) : (
              <Icon icon="chevronRight" />
            )}
          </div>
        )}
      </div>
      {isOpen && (
        <>
          {sanitizedOutput && (
            <ExpandedOutput title="Output">{sanitizedOutput}</ExpandedOutput>
          )}
          {sanitizedError && (
            <ExpandedOutput title="Error">{sanitizedError}</ExpandedOutput>
          )}
          {eventCodes?.length > 0 && (
            <ExpandedOutput title="Insights">
              {eventCodes.map((event) => (
                <FormattedEvent key={event.code} event={event} />
              ))}
            </ExpandedOutput>
          )}
        </>
      )}
    </section>
  )
}

export default Execution
